/**
 * Shared lobby code: room codes, ticketing, DDB key map, WS message types,
 * lobby settings, and replay-buffer event shapes. Imported by the realtime
 * Lambdas (ws-router, ws-connect, ws-disconnect), the lobby-mgr Lambda, and
 * the web client. Anything cross-tier that describes a lobby's shape lives
 * here.
 */
export * from './settings';
export * from './presence';
export * from './cosmetics-catalog';
export * from './cosmetic-resolver';
export * from './realtime';

/** R2#87 — Global title catalog. Every title that can exist in a player's
 *  inventory must be listed here. The characters lambda checks selectedTitle
 *  against BOTH inv.titles (per-character ownership) AND ALL_TITLES (global
 *  catalog) so a stale/deprecated title in inv can't slip through.
 *
 *  Sources unified here:
 *  - Default: 'Tavern Regular' (DEFAULT_INVENTORY in characters lambda).
 *  - deriveInventory `unlock('titles', ...)` direct grants.
 *  - MASTERY_GAMES_C × {Apprentice, Adept, Master} mastery tiers.
 *  - ACHIEVEMENTS_V2 perGameMilestone reward titles (`{ bucket: 'titles' }`).
 *
 *  Update this list whenever a new title is added anywhere in the codebase.
 *  Tests (post-launch) should verify deriveInventory's emitted titles are a
 *  subset of ALL_TITLES. */
export const ALL_TITLES: readonly string[] = [
  // Default starter title.
  'Tavern Regular',
  // deriveInventory direct unlocks.
  'First Winner',
  'Table Champion',
  'MVP Magnet',
  '100 Club',
  '1000 Club',
  'Streaker',
  'Daily Devotee',
  'Tavern Veteran',
  'Undefeated',
  // Mastery — 16 games × 3 tiers (matches MASTERY_GAMES_C in characters
  // lambda). Source-of-truth for the game name strings is the literal
  // copies here; if MASTERY_GAMES_C ever renames a game, both must update.
  'Tap Race Apprentice', 'Tap Race Adept', 'Tap Race Master',
  'Witch\'s Switch Apprentice', 'Witch\'s Switch Adept', 'Witch\'s Switch Master',
  'Witch Switch Apprentice', 'Witch Switch Adept', 'Witch Switch Master',
  'Tarot Tap Apprentice', 'Tarot Tap Adept', 'Tarot Tap Master',
  'Spice Rush Apprentice', 'Spice Rush Adept', 'Spice Rush Master',
  'Trace Race Apprentice', 'Trace Race Adept', 'Trace Race Master',
  'Ouija Roulette Apprentice', 'Ouija Roulette Adept', 'Ouija Roulette Master',
  'Ouija Apprentice', 'Ouija Adept', 'Ouija Master',
  'Quick Draw Apprentice', 'Quick Draw Adept', 'Quick Draw Master',
  'One Of Us Apprentice', 'One Of Us Adept', 'One Of Us Master',
  'One of Us Apprentice', 'One of Us Adept', 'One of Us Master',
  'Coin Conveyer Apprentice', 'Coin Conveyer Adept', 'Coin Conveyer Master',
  'Hexagon Heat Apprentice', 'Hexagon Heat Adept', 'Hexagon Heat Master',
  'Hexagon Apprentice', 'Hexagon Adept', 'Hexagon Master',
  'Last Lantern Apprentice', 'Last Lantern Adept', 'Last Lantern Master',
  'Lantern Apprentice', 'Lantern Adept', 'Lantern Master',
  'Lumber Tumble Apprentice', 'Lumber Tumble Adept', 'Lumber Tumble Master',
  'Lumber Apprentice', 'Lumber Adept', 'Lumber Master',
  'Climb the Spire Apprentice', 'Climb the Spire Adept', 'Climb the Spire Master',
  'Spire Apprentice', 'Spire Adept', 'Spire Master',
  'Burnstile Apprentice', 'Burnstile Adept', 'Burnstile Master',
  'Stick Brawl Apprentice', 'Stick Brawl Adept', 'Stick Brawl Master',
  'Brawl Apprentice', 'Brawl Adept', 'Brawl Master',
  'Draft Duel Apprentice', 'Draft Duel Adept', 'Draft Duel Master',
  'Draft Apprentice', 'Draft Adept', 'Draft Master',
  'Banner Bash Apprentice', 'Banner Bash Adept', 'Banner Bash Master',
  'Cellar Sweep Apprentice', 'Cellar Sweep Adept', 'Cellar Sweep Master',
  'Cipher Cellar Apprentice', 'Cipher Cellar Adept', 'Cipher Cellar Master',
  'Heist Heist Apprentice', 'Heist Heist Adept', 'Heist Heist Master',
  'Phantom Pursuit Apprentice', 'Phantom Pursuit Adept', 'Phantom Pursuit Master',
  'Pirate Pulley Apprentice', 'Pirate Pulley Adept', 'Pirate Pulley Master',
  'Plinko Tavern Apprentice', 'Plinko Tavern Adept', 'Plinko Tavern Master',
  'Quill Race Apprentice', 'Quill Race Adept', 'Quill Race Master',
  'Relay Forge Apprentice', 'Relay Forge Adept', 'Relay Forge Master',
  'Rope Walk Apprentice', 'Rope Walk Adept', 'Rope Walk Master',
  'Sky Sieve Apprentice', 'Sky Sieve Adept', 'Sky Sieve Master',
  'Splatter Square Apprentice', 'Splatter Square Adept', 'Splatter Square Master',
  'Stack Smack Apprentice', 'Stack Smack Adept', 'Stack Smack Master',
  'Trapline Apprentice', 'Trapline Adept', 'Trapline Master',
  // ACHIEVEMENTS_V2 perGameMilestone titles (titles bucket rewards).
  'Last One Lit', 'Card Sharp', 'Thunderclap', 'Heat Stroke', 'Spire Climber',
  'Spirit Speaker', 'Hex Master', 'Goldhand', 'Spicemaster', 'Truth Seer',
  'Steady Hand', 'Wolf in the Fold', 'First Pen', 'Quickfinger', 'Card Whisperer',
  'Bridge Runner', 'Banner Baron', 'Cellar Captain', 'Cipher Sage', 'Vault Ghost',
  'Phantom Mark', 'Deckhand Ace', 'Peg Whisperer', 'Quillmaster', 'Forge Captain',
  'Ropewalker', 'Sky Sifter', 'Paint Baron', 'Stack Boss', 'Trapwright',
  // Season Pass titles (S1 — Crimson Solstice).
  'Season 1 Patron', 'Season 1 Devotee',
  'Crimson Solstice', 'Crimson Solstice Champion',
  // Phase E1 — granted alongside the tier-60 Searing Mark face claim.
  // The cosmetic itself lives in SEASON_REWARDS[1].premium[59]; the
  // season-claim Lambda fans this title out as a side reward when the
  // Searing Mark face is claimed.
  'Lantern Bearer of the Crimson Solstice',
];

/** Lookup helper for ALL_TITLES (O(1) check). */
const ALL_TITLES_SET: ReadonlySet<string> = new Set(ALL_TITLES);
export function isKnownTitle(title: string): boolean {
  return ALL_TITLES_SET.has(title);
}

const CODE_ALPHABET = 'ABCDEFGHJKMNPQRSTUVWXYZ';
export const ROOM_CODE_LEN = 4;
// 8P Phase 3: the player cap lives in @tt/players (the single source of
// player identity); re-exported here for the many existing importers.
export { MAX_PLAYERS } from '@tt/players';

export function isValidRoomCode(code: string): boolean {
  if (typeof code !== 'string' || code.length !== ROOM_CODE_LEN) return false;
  return code.toUpperCase().split('').every((c) => CODE_ALPHABET.includes(c));
}

export type LobbyState =
  | 'waiting'        // pre-game, players ready up
  | 'voting'         // pre-first-round vote (all-ready -> vote tiles)
  | 'countdown'      // 3-2-1 before a round
  | 'playing'        // round in progress
  | 'roundEnd'       // post-round pane (vote/host/random next-game pick)
  | 'finalStandings' // end-of-set crown
  | 'ended';         // closed

export interface LobbyCharacterIcon {
  set: string;
  id: number;
  hue: number;
  body?: number;
  hat?: number;
  face?: number;
  accentHue?: number;
  /** New cosmetic slots (Phase A — season-lantern buildout). Optional;
   *  undefined = no decoration. Cosmetic ids reference entries in
   *  `COSMETIC_CATALOG` (kind: 'border' / 'background'). */
  border?: string;
  background?: string;
  /** Phase F5 — face-overlay mask. Semantic id matching MASKS in
   *  `web/src/features/characters/characterParts.tsx` (catalog id
   *  `mask-${id}`, inventory bucket `masks`). undefined = no mask. */
  mask?: string;
}

export type LobbyVisibility = 'public' | 'private';

export interface LobbyMetaItem {
  pk: string;
  sk: 'META';
  roomCode: string;
  gameId: string;             // current/next game
  hostConnId: string;
  hostCharId: string;
  hostDeviceId: string;
  /** 'public' rooms surface in the public browser via the GSI2 index;
   *  'private' is the historical default — joinable only by 4-letter code. */
  visibility?: LobbyVisibility;
  state: LobbyState;
  matchId?: string;           // current match
  matchStartsAt?: number;     // epoch ms
  matchEndsAt?: number;
  matchParams?: unknown;
  setRoundsPlayed: number;
  setScores: Record<string, number>; // charId -> running lobby score
  voteOptions?: string[];     // game ids on offer
  voteEndsAt?: number;
  votes?: Record<string, string>; // charId -> gameId
  bannedDevices?: string[];   // host-kicked, blocked from rejoin
  settings: import('./settings').LobbySettingsPartial;
  eventSeq: number;           // monotonic event counter for replay buffer
  /** "Your Own Tavern" Phase B — jukebox music queue. Single attribute on
   *  the META row (like `settings` / `setScores`), read-modify-written by
   *  the ws-router jukebox handlers. Absent on legacy rows = idle. */
  jukebox?: import('./records').JukeboxState;
  createdAt: string;
  ttl: number;
}

/** Public-lobby index row (lives alongside the META row). The writer
 *  creates this row only when visibility==='public' and refreshes it on
 *  state/seat-count changes; it's the only thing the public browser
 *  reads via GSI2. */
export interface PubLobbyItem {
  pk: string;            // PUB_LOBBY
  sk: string;            // <createdAtIso>#<roomCode>
  gsi2pk: 'PUB_LOBBY';
  gsi2sk: string;        // same as sk
  roomCode: string;
  gameId: string;
  hostName: string;
  hostIcon: LobbyCharacterIcon;
  liveSeats: number;     // 0..4
  state: LobbyState;
  createdAt: string;
  ttl: number;
}

export interface LobbyPlayerItem {
  pk: string;
  sk: string;
  connId: string;
  charId: string;
  charName: string;
  charIcon: LobbyCharacterIcon;
  /** Snapshot of selectedTitle at join — refreshed when player rejoins. */
  charTitle?: string | null;
  /** R2#88 — Snapshot of favoriteGameId at join. Surfaced on the seat tile
   *  so other players can see "I love witch-switch" before the lobby starts. */
  charFav?: string | null;
  charSeat?: import('./presence').SeatCosmetics;
  ready: boolean;
  /** Seat index, 0-based (plain number since 8P Phase 3). */
  slot: number;
  isSpectator: boolean;
  joinedAt: string;
  ttl: number;
  /** Last heartbeat ms; used for slow-conn indicator + quick rejoin. */
  lastSeenAt: number;
  /** When set + state==='disconnected', they're in quick-rejoin window. */
  disconnectedAt?: number;
}

export interface ConnItem {
  pk: string;
  sk: 'META';
  connId: string;
  charId: string;
  deviceId: string;
  charName: string;
  charIcon: LobbyCharacterIcon;
  charTitle?: string | null;
  /** R2#88 — favorite-game id snapshot for surfacing on lobby tiles. */
  charFav?: string | null;
  charSeat?: import('./presence').SeatCosmetics;
  roomCode: string;
  joinedAt: string;
  ttl: number;
}

export interface TicketItem {
  pk: string;
  sk: 'META';
  ticket: string;
  roomCode: string;
  charId: string;
  deviceId: string;
  charName: string;
  charIcon: LobbyCharacterIcon;
  charTitle?: string | null;
  /** R2#88 — favorite-game id snapshot, propagated CHAR meta → ticket → conn → lobby player. */
  charFav?: string | null;
  charSeat?: import('./presence').SeatCosmetics;
  createdAt: string;
  ttl: number;
}

export interface ReplayEventItem {
  pk: string;       // LOBBY#<code>
  sk: string;       // EVENT#<seqPadded>
  seq: number;
  payload: ServerEvent;
  ttl: number;
}

// ── Public lobby view (broadcast shape) ─────────────────────────────────

export interface LobbyPlayerView {
  charId: string;
  name: string;
  icon: LobbyCharacterIcon;
  /** Selected title — drives the seat-tile flair (gold border for Mastery). */
  title?: string | null;
  /** R2#88 — Favorite game id, surfaced on the seat tile. Propagated from
   *  CHAR meta on connect/refresh. Null when the player hasn't picked one. */
  favoriteGameId?: string | null;
  seatCosmetics?: import('./presence').SeatCosmetics;
  ready: boolean;
  /** Seat index, 0-based (plain number since 8P Phase 3). */
  slot: number;
  isHost: boolean;
  isSpectator: boolean;
  isDisconnected: boolean;
  lobbyScore: number;
  /** Heartbeat lag in ms; clients use this for the slow-conn indicator. */
  lagMs: number;
}

export interface LobbyView {
  roomCode: string;
  gameId: string;
  state: LobbyState;
  hostCharId: string;
  players: LobbyPlayerView[];
  settings: import('./settings').LobbySettings;
  setRoundsPlayed: number;
  matchEndsAt?: number;
  activeMatch?: {
    matchId: string;
    gameId: string;
    startsAt: number;
    endsAt: number;
    params: unknown;
  };
  /** "Your Own Tavern" Phase B — current jukebox queue + now-playing pick.
   *  Surfaced in the view so a late joiner gets state on the first
   *  `lobby.state` without waiting for the next `jukebox.state` event. */
  jukebox?: import('./records').JukeboxState;
}

// ── WS protocol ─────────────────────────────────────────────────────────

export type ClientAction =
  | { action: 'lobby.join' }
  | { action: 'lobby.replay'; sinceSeq: number }
  | { action: 'lobby.ready'; ready: boolean }
  | { action: 'lobby.start' }
  | { action: 'lobby.leave' }
  | { action: 'lobby.settings.set'; patch: Record<string, unknown> }
  | { action: 'set.new' }
  | { action: 'lobby.kick'; charId: string }
  | { action: 'free.launch'; gameId: string }
  | { action: 'vote.cast'; gameId: string }
  | { action: 'roundEnd.skip' }
  | { action: 'game.input'; payload: unknown }
  | { action: 'game.heartbeat' }
  | { action: 'reaction'; emoji: string }
  | { action: 'chat.msg'; text: string }
  | { action: 'jukebox.request'; recordId: string }
  | { action: 'jukebox.advance' }
  | { action: 'presence.refresh' }
  | { action: 'ping' };

export interface RoundResult {
  matchId: string;
  gameId: string;
  scores: Record<string, number>;     // charId -> raw round score
  ranks: string[];                    // charIds in finishing order
  awards: Array<{ charId: string; label: string }>; // MVP-style callouts
  lobbyDelta: Record<string, number>;  // charId -> normalized points added to this set
  globalDelta: Record<string, number>; // charId -> points added to global
  xp?: Record<string, RoundXp>;        // charId -> account XP earned this round + level transition
}

export interface RoundXp {
  items: Array<{ label: string; amount: number }>;
  total: number;
  accountXpBefore: number;
  accountXpAfter: number;
  levelBefore: number;
  levelAfter: number;
  toNext: { current: number; needed: number };
}

export type ServerEvent =
  | { event: 'lobby.state'; state: LobbyView; seq: number }
  | { event: 'chat.msg'; charId: string; name: string; text: string; at: string; seq: number }
  | { event: 'reaction'; charId: string; name: string; emoji: string; at: number; seq: number }
  /** "Your Own Tavern" Phase B — jukebox queue state, broadcast (durable
   *  ring buffer) on every mutation. `jukebox` is the full current state so
   *  clients replace rather than patch. */
  | { event: 'jukebox.state'; jukebox: import('./records').JukeboxState; at: number; seq: number }
  /** Transient waiting-room walk-around position update. Fanned out directly
   *  (NOT written to the replay ring buffer, NOT seq'd — positions are
   *  ephemeral; a reconnect just respawns at the player's seat). Low-rate:
   *  clients send ≤3/s while moving + one final rest message. */
  | { event: 'lobby.move'; charId: string; x: number; z: number; yaw: number; moving: boolean }
  /** Hall toy trigger (waiting-room social delight — dartboard / dice cup /
   *  tankard clink). Fanned out directly to every OTHER connection (NOT
   *  ring-buffered, NOT seq'd — pure transient ephemera, exactly like
   *  `lobby.move`). `seed` is an int 0..999 the ACTOR minted and the server
   *  relays verbatim, so the derived outcome (dart landing, dice pips, foam
   *  jitter) is byte-identical on every screen including the actor's own
   *  optimistic play. Zero gameplay impact. */
  | { event: 'toy.use'; charId: string; name: string; toyId: string; seed: number }
  | { event: 'game.start'; matchId: string; gameId: string; startsAt: number; endsAt: number; countdownMs: number; durationMs: number; params: unknown; seq: number }
  | { event: 'game.tick'; matchId: string; payload: unknown; seq?: number; ack?: number; ackBySlot?: Record<string, number> }
  /** RT fast-path offer (Total-Redesign-Spec §1d a3, flag-gated). Sent
   *  per-connection (NOT broadcast — rtToken is slot-scoped) right after
   *  game.start when RT_ENABLED on ws-router. Old clients ignore unknown
   *  events; new clients open a second WS to `rtUrl?token=rtToken` and
   *  prefer it for in-match traffic, with the API GW socket + DDB queue
   *  as the automatic per-slot fallback. */
  | { event: 'rt.offer'; matchId: string; gameId: string; rtUrl: string; rtToken: string; seq: number }
  /** 8P Phase 2 — LOBBY presence-lane offer. Sent per-connection to each
   *  seated player on (re)connect while RT is enabled; the client opens a
   *  second WS to the gameroom's `lobby:<roomCode>` lane and prefers it
   *  for waiting-room walk sync (`lobby.move`), with the deprecated
   *  Lambda relay as the automatic fallback. */
  | { event: 'rt.lobby'; roomCode: string; rtUrl: string; rtToken: string; seq: number }
  /** RT fast-lane control frames, sent only on the gameroom's direct /rt
   *  WebSocket (never on the main API-GW socket, never seq'd). The client's
   *  lane handler consumes these before delegating other frames to the
   *  shared event handler. `rt.welcome` confirms the lane end-to-end;
   *  `rt.pong` echoes an `rt.ping` for keepalive + clock-offset sampling. */
  | { event: 'rt.welcome'; matchId?: string; slot?: number; serverNow: number; caps?: string[] }
  | { event: 'rt.pong'; t: number; serverNow: number }
  | { event: 'game.phase'; matchId: string; phase: string; phaseEndsAt?: number; payload?: unknown; seq: number }
  | { event: 'game.end'; matchId: string; result: RoundResult; seq: number }
  | { event: 'vote.options'; gameIds: string[]; endsAt: number; seq: number }
  | { event: 'vote.tally'; tally: Record<string, number>; seq: number }
  | { event: 'set.final'; standings: Array<{ charId: string; name: string; points: number }>; awards: Array<{ charId: string; label: string }>; winnerHue: number; seq: number }
  | { event: 'tutorial'; gameId: string; pitch: string; tip: string; seq: number }
  | { event: 'kicked'; reason: string }
  | { event: 'pong'; t: number }
  | { event: 'error'; code: string; msg?: string };

export const KEY = {
  lobbyMeta: (code: string) => ({ pk: `LOBBY#${code}`, sk: 'META' as const }),
  /** Public-lobby projection row (separate item from LobbyMeta — fan-out write). */
  pubLobby: (createdAtIso: string, code: string) => ({
    pk: 'PUB_LOBBY' as const,
    sk: `${createdAtIso}#${code}`,
    gsi2pk: 'PUB_LOBBY' as const,
    gsi2sk: `${createdAtIso}#${code}`,
  }),
  lobbyPlayer: (code: string, connId: string) => ({ pk: `LOBBY#${code}`, sk: `PLAYER#${connId}` }),
  lobbyEvent: (code: string, seq: number) => ({ pk: `LOBBY#${code}`, sk: `EVENT#${String(seq).padStart(12, '0')}` }),
  match: (matchId: string) => ({ pk: `MATCH#${matchId}`, sk: 'META' as const }),
  matchState: (matchId: string) => ({ pk: `MATCH#${matchId}`, sk: 'STATE' as const }),
  matchPlayer: (matchId: string, charId: string) => ({ pk: `MATCH#${matchId}`, sk: `PLAYER#${charId}` }),
  conn: (connId: string) => ({ pk: `CONN#${connId}`, sk: 'META' as const }),
  ticket: (ticket: string) => ({ pk: `TICKET#${ticket}`, sk: 'META' as const }),
  achievement: (charId: string, code: string) => ({ pk: `CHAR#${charId}`, sk: `ACH#${code}` }),
  charGameStat: (charId: string, gameId: string) => ({ pk: `CHAR#${charId}`, sk: `GAME#${gameId}` }),
  activity: (charId: string, ts: number, rand: string) => ({ pk: `CHAR#${charId}`, sk: `ACT#${String(ts).padStart(13, '0')}-${rand}` }),
  leaderboard: (gameId: string) => `LB#${gameId}`,
};

export function leaderboardSk(score: number, charId: string): string {
  // 9999999999 - score, zero-padded so DDB sort orders highest score first.
  const inv = String(9_999_999_999 - Math.max(0, Math.min(9_999_999_999, Math.floor(score))));
  return `${inv.padStart(10, '0')}#${charId}`;
}
